import { extrudeAdditionalInfo, extrudeKeyInfo, type Property } from "@stepapp/core";
import { Block, Button, Divider, palette, spacing, Typography } from "@stepapp/ui";
import { useFocusEffect, useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FlatList, StyleSheet } from "react-native";
import { HouseAreaOutOfScopeSheet, PropertyItem } from "../../../src/components";
import { useAppDispatch, useAppSelector } from "../../../src/hooks";
import type { ReservationFormParams } from "../../../src/store/draftReservation/types";
import { setHomeSize } from "../../../src/store/homeProfile";

const keyExtractor = ({ propertyId }: Property) => propertyId;

const PickPropertyScreen = () => {
  const { t } = useTranslation();
  const properties = useAppSelector(({ user }) => user.properties);
  const { city, localizationId } = useAppSelector(({ appConfig }) => appConfig);
  const floorArea = useAppSelector(({ homeProfile }) => homeProfile.floorArea);
  const { slug } = useLocalSearchParams();
  const { navigate } = useRouter();
  const appDispatch = useAppDispatch();

  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<ReservationFormParams>();
  const [meta, propertyId] = watch(["meta", "propertyId"]);

  const filteredProperties = useMemo(
    () => properties.filter((item) => item.localizationId === localizationId),
    [properties, localizationId],
  );

  const property = useMemo(
    () => filteredProperties.find((property) => property.propertyId === propertyId),
    [filteredProperties, propertyId],
  );

  const handleSelectProperty = useCallback(
    (id: string) => {
      if (id === propertyId) return setValue("propertyId", "");
      setValue("propertyId", id, {
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    [setValue, propertyId],
  );

  useFocusEffect(
    useCallback(() => {
      if (!propertyId) {
        setValue("propertyId", filteredProperties[0]?.propertyId || "");
      }
    }, [propertyId, filteredProperties, setValue]),
  );

  const handleEditAddress = useCallback(() => {
    navigate(`/service/${slug}/add-address`);
  }, [navigate, slug]);

  const handleAddNewAddress = useCallback(() => {
    setValue("propertyId", "");
    setTimeout(() => navigate(`/service/${slug}/add-address`), 250);
  }, [navigate, setValue, slug]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: not required
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: todo
  useEffect(() => {
    const initialKeyInfo = extrudeKeyInfo(property?.notes) || "";
    setValue("address", {
      city: property?.city || city,
      doorCode: property?.doorCode || "",
      floorArea: property?.floorArea || floorArea,
      postalOrZipCode: property?.postalOrZipCode || "",
      streetAddress1: property?.streetAddress1 || "",
      streetAddress2: property?.streetAddress2 || "",
      notes: extrudeAdditionalInfo(property?.notes) || "",
      type: property?.homeType,
    });
    setValue("houseArea", property?.floorArea || floorArea, { shouldValidate: true });
    setValue("propertyId", property?.propertyId || "");
    setValue("locationId", property?.localizationId || localizationId);
    setValue("meta", {
      ...meta,
      homeTypePlaceholder: property?.homeType || "",
      willCustomerBePresent: initialKeyInfo
        ? initialKeyInfo === t("pages.serviceDateAndAddress.iWillBeThere")
        : "deselected",
      keyInfo:
        initialKeyInfo === t("pages.serviceDateAndAddress.iWillBeThere") ? "" : initialKeyInfo,
    });
    appDispatch(setHomeSize(property?.floorArea || 0));
  }, [propertyId]);

  const renderItem = useCallback(
    ({ item, index }: { item: Property; index: number }) => (
      <Block marginBottom={2} testID={`property-${index}`}>
        <PropertyItem
          {...item}
          editButtonPress={handleEditAddress}
          selectItem={() => handleSelectProperty(item.propertyId)}
          selected={item.propertyId === propertyId}
        />
      </Block>
    ),
    [handleEditAddress, handleSelectProperty, propertyId],
  );

  return (
    <>
      <FlatList
        ListFooterComponent={() => (
          <Button
            containerStyle={styles.addAddressButton}
            disabled={properties.length >= 10}
            label={t("screens.homeEdit.addAddress")}
            onPress={handleAddNewAddress}
            type="primary"
          />
        )}
        ListHeaderComponent={() => (
          <Block paddingBottom={3} paddingTop={2}>
            <Typography variant="newH2">{t("screens.service.whichAddress")}</Typography>
            <Block paddingBottom={2} paddingTop={1}>
              <Typography type="secondary" variant="smallLabel">
                {t("screens.service.pickOrCreateAddress")}
              </Typography>
            </Block>
            <Divider spaceless color={palette.secondary.grey} />
          </Block>
        )}
        contentContainerStyle={styles.flatListContent}
        data={filteredProperties}
        keyExtractor={keyExtractor}
        keyboardDismissMode="on-drag"
        renderItem={renderItem}
      />

      <HouseAreaOutOfScopeSheet formError={errors?.houseArea} />
    </>
  );
};

export default PickPropertyScreen;

const styles = StyleSheet.create({
  addAddressButton: {
    marginBottom: spacing.unit * 15,
    marginLeft: spacing.unit * 6,
    marginRight: spacing.unit * 6,
  },
  flatListContent: {
    paddingTop: spacing.unit * 2,
  },
});
